import React, { useEffect, useMemo, useRef } from "react";
import { Box, Button, Stack, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import type { SxProps, Theme } from "@mui/material/styles";
import { Flag } from "@phosphor-icons/react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Legend,
  Tooltip,
  type ChartData,
  type ChartOptions,
  type Plugin,
} from "chart.js";
import { Bar } from "react-chartjs-2";

ChartJS.register(CategoryScale, LinearScale, BarElement, Legend, Tooltip);

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type StepStatus = "mastered" | "emerging" | "learning" | "not-started";

export interface StepBreakdownRow {
  /** Step description, e.g. "Rinse toothbrush" */
  step: string;
  /** Step position in the original task analysis chain, e.g. 8 */
  stepNumber: number;
  /** First session the step was worked on (1-based) */
  startSession: number;
  /** Last session the step was worked on (inclusive) */
  endSession: number;
  /** Current acquisition status — drives bar color and the right-hand label */
  status: StepStatus;
}

export interface TargetStepBreakdownChartProps {
  /** Descriptive caption above the chart */
  title: string;
  /** One row per task-analysis step, top to bottom */
  rows: StepBreakdownRow[];
  /** Total session count — sets the x-axis range. Defaults to the max endSession. */
  totalSessions?: number;
  /** Callback when "Add Marker" is clicked — button hidden when omitted */
  onAddMarker?: () => void;
  /** Draws the status legend inside the canvas (instead of the DOM legend) so it survives image snapshots. */
  showCanvasLegend?: boolean;
  /** Fires with the underlying Chart.js instance on mount, and with null on unmount. */
  onChartReady?: (chart: ChartJS<"bar"> | null) => void;
  /** Additional MUI sx overrides */
  sx?: SxProps<Theme>;
}

const fontFamily = "BasierCircle, sans-serif";

const LEGEND_STATUSES: StepStatus[] = ["mastered", "emerging", "learning", "not-started"];

const BAR_HEIGHT = 20;
const STATUS_LABEL_WIDTH = 88;

interface StatusStyle {
  legendLabel: string;
  rowLabel: string;
  barColor: string;
  /** Darker cap drawn at the end of mastered bars */
  capColor?: string;
  labelColor: string;
}

function buildStatusStyles(palette: any): Record<StepStatus, StatusStyle> {
  return {
    mastered: {
      legendLabel: "Mastered",
      rowLabel: "Mastered",
      barColor: palette.green?.[400] ?? "#66BB6A",
      capColor: palette.green?.[600] ?? "#43A047",
      labelColor: palette.success?.light ?? "#4CAF50",
    },
    emerging: {
      legendLabel: "Emerging",
      rowLabel: "Emerging",
      barColor: palette.blue?.[400] ?? "#42A5F5",
      labelColor: palette.blue?.[400] ?? "#42A5F5",
    },
    learning: {
      legendLabel: "Learning",
      rowLabel: "Learning",
      barColor: palette.orange?.[400] ?? "#FFA726",
      labelColor: palette.orange?.[400] ?? "#FFA726",
    },
    "not-started": {
      legendLabel: "Not started",
      rowLabel: "Not Started",
      barColor: palette.grayscale?.[400] ?? "#A1ACB9",
      labelColor: palette.grayscale?.[600] ?? "#798397",
    },
  };
}

// ---------------------------------------------------------------------------
// Plugins
// ---------------------------------------------------------------------------

/** Fills a rounded rect, falling back to a plain rect when roundRect is unavailable. */
function fillRoundedRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number
) {
  if (typeof ctx.roundRect === "function") {
    ctx.beginPath();
    ctx.roundRect(x, y, w, h, r);
    ctx.fill();
  } else {
    ctx.fillRect(x, y, w, h);
  }
}

/** Full-width grey track behind each row, per the Figma design. */
function buildTrackPlugin(rowCount: number, trackColor: string): Plugin<"bar"> {
  return {
    id: "stepBreakdownTracks",
    beforeDatasetsDraw(chart) {
      const { ctx, scales, chartArea } = chart;
      const yScale = scales["y"];
      if (!yScale || !chartArea) return;

      ctx.save();
      ctx.fillStyle = trackColor;
      for (let i = 0; i < rowCount; i++) {
        const y = yScale.getPixelForValue(i);
        fillRoundedRect(
          ctx,
          chartArea.left,
          y - BAR_HEIGHT / 2,
          chartArea.right - chartArea.left,
          BAR_HEIGHT,
          4
        );
      }
      ctx.restore();
    },
  };
}

/** Status label to the right of each row, colored per status. */
function buildStatusLabelPlugin(
  rows: StepBreakdownRow[],
  styles: Record<StepStatus, StatusStyle>
): Plugin<"bar"> {
  return {
    id: "stepBreakdownStatusLabels",
    afterDraw(chart) {
      const { ctx, scales, chartArea } = chart;
      const yScale = scales["y"];
      if (!yScale || !chartArea) return;

      ctx.save();
      ctx.font = `400 12px ${fontFamily}`;
      ctx.textAlign = "left";
      ctx.textBaseline = "middle";
      rows.forEach((row, i) => {
        const y = yScale.getPixelForValue(i);
        ctx.fillStyle = styles[row.status].labelColor;
        ctx.fillText(styles[row.status].rowLabel, chartArea.right + 8, y);
      });
      ctx.restore();
    },
  };
}

/** Darker end cap on mastered bars, per the Figma design. */
function buildMasteredCapPlugin(
  rows: StepBreakdownRow[],
  styles: Record<StepStatus, StatusStyle>
): Plugin<"bar"> {
  return {
    id: "stepBreakdownMasteredCaps",
    afterDatasetsDraw(chart) {
      const { ctx, scales } = chart;
      const xScale = scales["x"];
      const yScale = scales["y"];
      if (!xScale || !yScale) return;

      ctx.save();
      rows.forEach((row, i) => {
        const cap = styles[row.status].capColor;
        if (!cap || row.status !== "mastered") return;
        const endX = xScale.getPixelForValue(row.endSession);
        const y = yScale.getPixelForValue(i);
        ctx.fillStyle = cap;
        fillRoundedRect(ctx, endX - 12, y - BAR_HEIGHT / 2, 12, BAR_HEIGHT, 4);
      });
      ctx.restore();
    },
  };
}

// ---------------------------------------------------------------------------
// TargetStepBreakdownChart
// ---------------------------------------------------------------------------

/**
 * Gantt-style chart for task-analysis targets — one floating bar per step
 * spanning the sessions it was worked on, colored by acquisition status
 * (mastered / emerging / learning), with a grey track per row and the status
 * label on the right. Sessions run along the top axis.
 *
 * @param props
 * @param props.title - Descriptive caption above the chart
 * @param props.rows - One row per task-analysis step
 * @param [props.totalSessions] - X-axis range; defaults to the max endSession
 * @param [props.onAddMarker] - Add marker callback (hides button when omitted)
 * @param [props.showCanvasLegend] - Render the status legend inside the canvas
 * @param [props.onChartReady] - Fires with the Chart.js instance on mount, null on unmount
 * @param [props.sx] - Additional MUI sx overrides
 */
const TargetStepBreakdownChart: React.FC<TargetStepBreakdownChartProps> = ({
  title,
  rows,
  totalSessions,
  onAddMarker,
  showCanvasLegend = false,
  onChartReady,
  sx,
}) => {
  const theme = useTheme();
  const chartRef = useRef<ChartJS<"bar"> | null>(null);

  const setChartRef = (chart: ChartJS<"bar"> | null | undefined) => {
    const normalized = chart ?? null;
    chartRef.current = normalized;
    onChartReady?.(normalized);
  };

  // Cleanup the Chart instance on unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      chartRef.current?.destroy();
    };
  }, []);

  const p = theme.palette as any;
  const axisColor: string = p.grayscale?.[600] ?? "#798397";
  const trackColor: string = p.grayscale?.[50] ?? "#F6F7F8";

  const statusStyles = useMemo(() => buildStatusStyles(p), [p]);

  const maxSession = useMemo(
    () => totalSessions ?? Math.max(1, ...rows.map((r) => r.endSession)),
    [totalSessions, rows]
  );

  // Floating bars: [start, end] per row; not-started rows render no bar.
  const chartData: ChartData<"bar", ([number, number] | null)[]> = useMemo(
    () => ({
      labels: rows.map((r) => `${r.stepNumber}. ${r.step}`),
      datasets: [
        {
          label: title,
          data: rows.map((r) =>
            r.status === "not-started" ? null : [r.startSession, r.endSession]
          ),
          backgroundColor: rows.map((r) => statusStyles[r.status].barColor),
          borderRadius: 4,
          borderSkipped: false,
          barThickness: BAR_HEIGHT,
        },
      ],
    }),
    [rows, title, statusStyles]
  );

  const chartOptions: ChartOptions<"bar"> = useMemo(
    () => ({
      indexAxis: "y" as const,
      responsive: true,
      maintainAspectRatio: false,
      // Disabled intentionally — clinical data charts don't benefit from
      // intro animations and the instant render avoids a flash on re-mount.
      animation: false,
      layout: { padding: { top: 0, right: STATUS_LABEL_WIDTH, bottom: 0, left: 0 } },
      scales: {
        x: {
          display: true,
          position: "top" as const,
          min: 1,
          max: maxSession,
          grid: { display: false },
          border: { display: false },
          ticks: {
            stepSize: 3,
            color: axisColor,
            font: { size: 12, family: fontFamily },
            padding: 4,
            callback: (value) => `S${value}`,
          },
        },
        y: {
          display: true,
          grid: { display: false },
          border: { display: false },
          ticks: {
            color: axisColor,
            font: { size: 12, family: fontFamily },
            padding: 8,
          },
        },
      },
      plugins: {
        legend: showCanvasLegend
          ? {
              display: true,
              position: "top" as const,
              align: "start" as const,
              onClick: () => undefined,
              labels: {
                boxWidth: 8,
                boxHeight: 8,
                color: axisColor,
                font: { size: 12, family: fontFamily },
                generateLabels: () =>
                  LEGEND_STATUSES.map((status) => ({
                    text: statusStyles[status].legendLabel,
                    fillStyle: statusStyles[status].barColor,
                    strokeStyle: statusStyles[status].barColor,
                    lineWidth: 0,
                  })),
              },
            }
          : { display: false },
        tooltip: {
          enabled: true,
          backgroundColor: theme.palette.background?.paper ?? "#fff",
          titleColor: theme.palette.text.primary,
          bodyColor: theme.palette.text.secondary,
          borderColor: theme.palette.divider,
          borderWidth: 1,
          cornerRadius: 8,
          padding: 10,
          titleFont: { size: 12, family: fontFamily },
          bodyFont: { size: 12, family: fontFamily },
          callbacks: {
            label: (context) => {
              const row = rows[context.dataIndex];
              if (!row || row.status === "not-started") {
                return statusStyles["not-started"].rowLabel;
              }
              return `S${row.startSession} – S${row.endSession} · ${statusStyles[row.status].rowLabel}`;
            },
          },
        },
      },
    }),
    [axisColor, maxSession, rows, statusStyles, theme, showCanvasLegend]
  );

  const trackPlugin = useMemo(() => buildTrackPlugin(rows.length, trackColor), [rows, trackColor]);
  const statusLabelPlugin = useMemo(
    () => buildStatusLabelPlugin(rows, statusStyles),
    [rows, statusStyles]
  );
  const masteredCapPlugin = useMemo(
    () => buildMasteredCapPlugin(rows, statusStyles),
    [rows, statusStyles]
  );

  return (
    <Box
      component="section"
      aria-label="Step-level breakdown chart"
      sx={[
        {
          display: "flex",
          flexDirection: "column",
          gap: 4,
          bgcolor: "background.paper",
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 3,
          p: 6,
          width: "100%",
        },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    >
      {/* Header row */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="flex-end"
        spacing={2}
        sx={{ width: "100%" }}
      >
        <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
          {title}
        </Typography>

        {onAddMarker && (
          <Button
            variant="text"
            size="small"
            color="primary"
            startIcon={<Flag size={16} />}
            onClick={onAddMarker}
          >
            Add Marker
          </Button>
        )}
      </Stack>

      {/* Status legend */}
      {!showCanvasLegend && (
        <Stack direction="row" alignItems="center" gap={5} component="ul" sx={{ m: 0, p: 0 }}>
          {LEGEND_STATUSES.map((status) => (
            <Stack
              key={status}
              component="li"
              direction="row"
              alignItems="center"
              gap={1}
              sx={{ listStyle: "none" }}
            >
              <Box
                aria-hidden
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "3px",
                  bgcolor: statusStyles[status].barColor,
                }}
              />
              <Typography variant="body2" color="text.secondary">
                {statusStyles[status].legendLabel}
              </Typography>
            </Stack>
          ))}
        </Stack>
      )}

      {/* Chart area */}
      <Box aria-label="Chart area" sx={{ position: "relative", width: "100%", height: 300 }}>
        <Bar
          ref={setChartRef}
          data={chartData}
          options={chartOptions}
          plugins={[trackPlugin, statusLabelPlugin, masteredCapPlugin]}
        />
      </Box>
    </Box>
  );
};

export default TargetStepBreakdownChart;
